import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const UPDATED = "5 February 2026";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-accent transition-colors"
        data-testid="privacy-back"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>
      <p className="mt-8 text-primary font-semibold uppercase tracking-wider text-xs">Legal</p>
      <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2 tracking-tight">
        Privacy policy
      </h1>
      <p className="text-sm text-muted-foreground mt-3">Last updated: {UPDATED}</p>

      <div className="prose-fr mt-10 space-y-8 text-accent/85 leading-relaxed">
        <Section title="Overview">
          <p>
            Fetchright is committed to protecting your privacy. We handle personal information in
            accordance with the <strong>Australian Privacy Principles</strong> (APPs) under the{" "}
            <em>Privacy Act 1988 (Cth)</em>. This page tells you what we collect, why, and your rights.
          </p>
        </Section>

        <Section title="What we collect">
          <p>Fetchright is used <strong>anonymously — no account, no sign-in, no cookies for tracking</strong>. When you interact with certain features, we collect:</p>
          <ul className="list-disc pl-6 mt-3 space-y-1.5">
            <li>
              <strong>Quiz answers</strong> — the multiple-choice responses to our 15-question quiz.
              Stored temporarily to compute your matches; saved to your device library only if you tap
              "Save results".
            </li>
            <li>
              <strong>Anonymous device ID</strong> — a random identifier generated in your browser. It
              contains no personal information and is used only to remember your saves and your $2 unlock
              on this device.
            </li>
            <li>
              <strong>Favourites &amp; saved quizzes</strong> — the breed IDs you save and quiz results you
              save, linked to your anonymous device ID.
            </li>
            <li>
              <strong>Payment metadata</strong> — for Fetchright Plus subscribers, we store your Stripe
              customer ID, subscription ID, and premium status. We do <strong>not</strong> store card
              numbers, CVCs, or bank details — those go directly to Stripe.
            </li>
            <li>
              <strong>Chat messages</strong> — messages you send to the AI advisor are kept for the
              duration of the session so the model has conversational context. Sessions expire after 24
              hours of inactivity.
            </li>
            <li>
              <strong>Basic server logs</strong> — IP address, browser, timestamps, request path. Retained
              for up to 30 days for security and abuse prevention.
            </li>
          </ul>
        </Section>

        <Section title="Why we collect it">
          <ul className="list-disc pl-6 mt-2 space-y-1.5">
            <li>To provide the breed-matching, save, and subscription features you request.</li>
            <li>To operate and improve the service (aggregate, non-identifying analytics).</li>
            <li>To detect and prevent fraud, abuse, or misuse.</li>
            <li>To respond to your questions when you contact us.</li>
          </ul>
          <p className="mt-3">
            We do <strong>not</strong> sell your personal information to any third party.
          </p>
        </Section>

        <Section title="Who we share with (sub-processors)">
          <p>We use a small number of trusted third parties to run Fetchright. Each has their own privacy policy which we recommend you review.</p>
          <ul className="list-disc pl-6 mt-3 space-y-1.5">
            <li><strong>Stripe Payments Australia Pty Ltd</strong> — processes the $2 Fetchright Unlock payment.</li>
            <li><strong>Anthropic (Claude AI)</strong> — powers the AI advisor. Your chat messages are sent to Anthropic for response generation; not used for training.</li>
            <li><strong>MongoDB Atlas</strong> — encrypted database hosting.</li>
            <li><strong>Emergent Cloud</strong> — application hosting infrastructure.</li>
            <li><strong>PetRescue.com.au</strong> — surfaces publicly-listed adoptable dogs. No personal information is shared with them.</li>
          </ul>
        </Section>

        <Section title="Where your data lives">
          <p>
            Data is stored on secure servers, primarily in the Asia-Pacific region. Some sub-processors
            (Stripe, Anthropic) may process data in the United States or European Union under
            standard cross-border safeguards.
          </p>
        </Section>

        <Section title="Your rights">
          <p>Under the Australian Privacy Principles, you can request to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1.5">
            <li>Access the personal information we hold about you.</li>
            <li>Correct anything that is out of date or inaccurate.</li>
            <li>Delete your saved data (favourites, saved quizzes) for your device.</li>
          </ul>
          <p className="mt-3">
            Email <a className="text-primary underline" href="mailto:privacy@fetchright.net">privacy@fetchright.net</a>{" "}
            with your request. We respond within 14 days.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            We use one essential cookie: <code className="bg-muted rounded px-1 text-xs">session_token</code>{" "}
            (httpOnly, secure, same-site none) to keep you signed in. No advertising, tracking, or
            analytics cookies are set by Fetchright itself. Stripe may set its own cookies during
            checkout to prevent fraud.
          </p>
        </Section>

        <Section title="Children">
          <p>
            Fetchright is not directed at children under 15. We do not knowingly collect personal
            information from anyone under 15. If you believe a child has provided us information,
            email <a className="text-primary underline" href="mailto:privacy@fetchright.net">privacy@fetchright.net</a>{" "}
            and we will delete it.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            Material changes to this policy will be posted at{" "}
            <Link to="/privacy" className="text-primary underline">fetchright.net/privacy</Link>{" "}
            with an updated "Last updated" date. Continued use after changes constitutes acceptance.
          </p>
        </Section>

        <Section title="Complaints">
          <p>
            If you're not satisfied with how we handle a privacy concern, you can complain to the{" "}
            <a
              className="text-primary underline"
              href="https://www.oaic.gov.au"
              target="_blank"
              rel="noopener noreferrer"
            >
              Office of the Australian Information Commissioner (OAIC)
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl text-accent mb-3">{title}</h2>
      <div className="text-[15px]">{children}</div>
    </section>
  );
}
